import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import RegisterHeader from "./Common/Header";
import RegisterSteps from "./Common/Steps";
import { Button } from "@mui/material";
import { useRegister } from "../../context/RegisterContext";
import ReactGA from "react-ga4";
import "./Register.scss";

interface SurveyData {
  tipoLavagem: string;
  servicosAdicionais: string[];
  areaEspera: string;
  bebidas: string[];
}

const SurveyPage = () => {
  const navigate = useNavigate();
  const { setSurveyData } = useRegister();
  
  const [tipoLavagem, setTipoLavagem] = useState<string>("");
  const [servicosAdicionais, setServicosAdicionais] = useState<string[]>([]);
  const [areaEspera, setAreaEspera] = useState<string>("");
  const [bebidas, setBebidas] = useState<string[]>([]);

  const steps = [
    'Endereço',
    'Responsável',
    'Negócio',
    'Explicação',
    'Pesquisa',
    'Categoria',
    'Login',
    'Confirmação'
  ];

  const handleServicoToggle = (servico: string) => {
    setServicosAdicionais((prev) => 
      prev.includes(servico) ? prev.filter((s) => s !== servico) : [...prev, servico]
    );
  };

  const handleBebidaToggle = (bebida: string) => {
    if (bebida === "nao-oferece") {
      setBebidas(["nao-oferece"]);
    } else {
      setBebidas((prev) => {
        const filtered = prev.filter((b) => b !== "nao-oferece");
        return filtered.includes(bebida) 
          ? filtered.filter((b) => b !== bebida) 
          : [...filtered, bebida];
      });
    }
  };

  const isFormValid = tipoLavagem && servicosAdicionais.length > 0 && areaEspera && bebidas.length > 0;

  const handleContinue = () => {
    if (!isFormValid) return;

    const surveyData: SurveyData = {
      tipoLavagem,
      servicosAdicionais,
      areaEspera,
      bebidas
    };

    console.log("💾 SALVANDO DADOS DA PESQUISA:", surveyData);
    setSurveyData(surveyData);

    ReactGA.event("survey_continue_click", {
      event: 'survey_continue_click',
      button_id: 'survey_continue',
      page_path: window.location.pathname,
      survey_data: surveyData
    });

    navigate("/register/category");
  };

  const handleBack = () => {
    navigate("/register/explanation");
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#E8EBF3',
      color: '#0f172a'
    }}>
      {/* Header */}
      <RegisterHeader />
      
      {/* Progress Steps */}
      <div style={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        paddingTop: '80px'
      }}>
        <div style={{ 
          maxWidth: '1280px', 
          margin: '0 auto', 
          padding: '32px 16px' 
        }}>
          <RegisterSteps currentStep={5} steps={steps} />
        </div>
      </div>

      <main style={{ padding: '32px 16px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Card principal */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
          }}>
            {/* Título */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h1 style={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#0f172a',
                marginBottom: '8px'
              }}>
                Pesquisa de Serviços
              </h1>
              <p style={{ 
                fontSize: '1rem',
                color: '#64748b'
              }}>
                Informe os serviços e benefícios que seu lava-rápido oferece
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {/* Tipo de Lavagem */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                <h3 style={{ 
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0f172a'
                }}>
                  1. Tipo de Lavagem
                </h3>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#64748b'
                }}>
                  Informe o tipo principal de lavagem que o seu lava-rápido realiza.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
                  {[
                    { value: "padrao", label: "Lavagem padrão estilo lava-jato" },
                    { value: "detalhada", label: "Lavagens mais detalhadas (ex: enceramento, polimento simples, higienização)" },
                    { value: "avancada", label: "Lavagens com estética avançada (ex: vitrificação, polimento premium, higienização completa)" },
                    { value: "exclusiva", label: "Lavagens exclusivas (ex: tratamentos especiais, cristalização, blindagem de pintura)" }
                  ].map((option) => (
                    <label 
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: tipoLavagem === option.value ? '#eff6ff' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (tipoLavagem !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (tipoLavagem !== option.value) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="tipoLavagem"
                        value={option.value}
                        checked={tipoLavagem === option.value}
                        onChange={(e) => setTipoLavagem(e.target.value)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ 
                        color: '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Serviços Adicionais */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                <h3 style={{ 
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0f172a'
                }}>
                  2. Serviços Adicionais
                </h3>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#64748b'
                }}>
                  Selecione todos os serviços extras que o seu estabelecimento oferece além da lavagem padrão.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
                  {[
                    { id: "cera-pretinho", label: "Cera e Pretinho" },
                    { id: "hidratacao-couro", label: "Hidratação de couro" },
                    { id: "higienizacao-interna", label: "Higienização interna completa" },
                    { id: "polimento", label: "Polimento" },
                    { id: "estetica-basica", label: "Serviços de estética (ex.: estética automotiva, pequenos reparos)" },
                    { id: "vitrificacao", label: "Vitrificação" },
                    { id: "higienizacao-premium", label: "Higienização premium" },
                    { id: "diferenciais-exclusivos", label: "Diferenciais exclusivos (ex.: cristalização, blindagem, aromatização premium)" }
                  ].map((servico) => (
                    <label 
                      key={servico.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        textAlign: 'left',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: servicosAdicionais.includes(servico.id) ? '#eff6ff' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!servicosAdicionais.includes(servico.id)) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!servicosAdicionais.includes(servico.id)) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={servicosAdicionais.includes(servico.id)}
                        onChange={() => handleServicoToggle(servico.id)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ 
                        color: '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        {servico.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Área de Espera */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                <h3 style={{ 
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0f172a'
                }}>
                  3. Área de Espera
                </h3>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#64748b'
                }}>
                  Marque a opção que melhor descreve o espaço que os clientes têm enquanto aguardam o serviço.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { value: "nao-possui", label: "Não possui área de espera" },
                    { value: "espaco-sentar", label: "Espaço para sentar (banco, cadeira simples)" },
                    { value: "estrutura-completa", label: "Estrutura completa (área coberta, cadeiras confortáveis)" },
                    { value: "climatizada", label: "Climatizada com ar-condicionado / vidro / lounge" }
                  ].map((option) => (
                    <label 
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: areaEspera === option.value ? '#eff6ff' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (areaEspera !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (areaEspera !== option.value) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="areaEspera"
                        value={option.value}
                        checked={areaEspera === option.value}
                        onChange={(e) => setAreaEspera(e.target.value)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ 
                        color: '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Bebidas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                <h3 style={{ 
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0f172a'
                }}>
                  4. Bebidas
                </h3>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#64748b'
                }}>
                  Selecione as opções de bebidas ou cortesias que você disponibiliza aos clientes.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { id: "nao-oferece", label: "Não oferece bebidas" },
                    { id: "agua", label: "Água" },
                    { id: "cafe", label: "Café" },
                    { id: "cha", label: "Chá / Refrigerante / Bebidas simples" },
                    { id: "diferenciais", label: "Bebidas diferenciadas (snacks, cápsulas, café especial, etc.)" }
                  ].map((bebida) => (
                    <label 
                      key={bebida.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: bebidas.includes(bebida.id) ? '#eff6ff' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!bebidas.includes(bebida.id)) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!bebidas.includes(bebida.id)) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={bebidas.includes(bebida.id)}
                        onChange={() => handleBebidaToggle(bebida.id)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ 
                        color: '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        {bebida.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Botões */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginTop: '32px',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row'
            }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                style={{
                  flex: 1,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  fontWeight: '500',
                  backgroundColor: 'transparent',
                  padding: '12px 24px',
                  borderRadius: '8px'
                }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={!isFormValid}
                style={{
                  flex: 1,
                  backgroundColor: isFormValid ? '#2563eb' : '#d1d5db',
                  color: isFormValid ? 'white' : '#9ca3af',
                  fontWeight: '500',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Continuar
              </Button>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '16px' }}>
            <p style={{ 
              color: '#64748b',
              fontSize: '1rem',
              margin: 0
            }}>
              Ficou alguma dúvida?{" "}
              <a 
                href="/fale-conosco" 
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Fale Conosco
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SurveyPage;
